import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type IFileRejection,
    type IUploadFile,
    FileUpload
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "FileUpload";

const USAGE_CODE = `const [files, setFiles] = useState<Array<IUploadFile>>([]);

<FileUpload
    accept={[".pdf", ".docx"]}
    maxSize={1024 * 1024}
    onChange={setFiles}
    isMultiple={true}
    title="Documents"
    files={files}
    maxFiles={4}
/>`;

const VARIANTS_CODE = `<FileUpload
    accept={[".pdf"]}
    title="Contract"
    variant="button"
    onChange={setFiles}
    files={files}
/>

<FileUpload
    accept={["image/png", "image/jpeg"]}
    title="Ada Lovelace"
    variant="avatar"
    onChange={setFiles}
    files={files}
    size="large"
/>`;

const RULES_CODE = `const [rejections, setRejections] = useState<Array<IFileRejection>>([]);

<FileUpload
    accept={["image/png", "image/jpeg"]}
    onReject={setRejections}
    maxSize={500 * 1024}
    onChange={setFiles}
    files={files}
/>`;

const STATES_CODE = `const handleProgress = (key: string, progress: number) => {
    setFiles((current) => current.map((file) => file.key === key ? {
        ...file,
        status: progress === 100 ? "done" : "uploading",
        progress
    } : file));
};`;

const GALLERY_CODE = `<FileUpload
    accept={["image/png", "image/jpeg"]}
    title="Campaign photos"
    onChange={setFiles}
    isMultiple={true}
    files={files}
    maxFiles={6}
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const DOCUMENT_ACCEPT = [
    ".pdf",
    ".docx"
];

const CONTRACT_ACCEPT = [
    ".pdf"
];

const IMAGE_ACCEPT = [
    "image/png",
    "image/jpeg"
];

const KILOBYTE = 1024;

const MEGABYTE = KILOBYTE * KILOBYTE;

const DOCUMENT_MAX_SIZE = MEGABYTE;

const DOCUMENT_MAX_FILES = 4;

const CONTRACT_MAX_SIZE = 10 * MEGABYTE;

const COVER_MAX_SIZE = 500 * KILOBYTE;

const AVATAR_MAX_SIZE = 5 * MEGABYTE;

const GALLERY_MAX_FILES = 6;

const UPLOAD_TICK = 500;

const UPLOAD_STEP = 25;

const isPending = (file: IUploadFile) => file.status === "queued" || file.status === "uploading";

const advanceFile = (file: IUploadFile): IUploadFile => {
    if(file.status === "queued") {
        return {
            ...file,
            status: "uploading",
            progress: 0
        };
    }

    if(file.status !== "uploading") {
        return file;
    }

    const progress = Math.min((file.progress ?? 0) + UPLOAD_STEP, 100);

    return {
        ...file,
        status: progress === 100 ? "done" : "uploading",
        progress
    };
};

const createNoFiles = (): Array<IUploadFile> => [];

const useUploadFiles = (createFiles: () => Array<IUploadFile> = createNoFiles) => {
    const [
        files,
        setFiles
    ] = useState<Array<IUploadFile>>(createFiles);

    useEffect(() => {
        if(!files.some(isPending)) {
            return;
        }

        const timer = window.setTimeout(() => {
            setFiles((current) => current.map(advanceFile));
        }, UPLOAD_TICK);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        files
    ]);

    return {
        setFiles,
        files
    };
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("fileUploadPage.rulesTitle"),
        id: "rules"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("fileUploadPage.galleryTitle"),
        id: "gallery"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const StatesDemo = () => {
    const {
        t
    } = useDocsI18n();

    const {
        setFiles,
        files
    } = useUploadFiles(() => [
        {
            name: t("fileUploadPage.fileBudget"),
            size: 860 * KILOBYTE,
            status: "processing",
            key: "budget"
        },
        {
            name: t("sample.fileName"),
            size: 1.2 * MEGABYTE,
            status: "done",
            key: "report"
        },
        {
            errorText: t("fileUploadPage.errorConnection"),
            name: t("fileUploadPage.fileDraft"),
            size: 1.4 * MEGABYTE,
            status: "error",
            key: "draft"
        }
    ]);

    const [
        disabledFiles
    ] = useState<Array<IUploadFile>>(() => [
        {
            name: t("sample.fileName"),
            size: 1.2 * MEGABYTE,
            status: "done",
            key: "report"
        }
    ]);

    const handleRetry = (key: string) => {
        setFiles((current) => current.map((file) => file.key === key ? {
            ...file,
            errorText: undefined,
            status: "uploading",
            progress: 0
        } : file));
    };

    return <>
        <Preview
            caption={t("fileUploadPage.statesCaption")}
            isStacked={true}
        >
            <FileUpload
                title={t("fileUploadPage.documents")}
                accept={DOCUMENT_ACCEPT}
                onRetry={handleRetry}
                onChange={setFiles}
                isMultiple={true}
                files={files}
            />
        </Preview>
        <Preview
            caption={t("fileUploadPage.disabledCaption")}
            isStacked={true}
        >
            <FileUpload
                title={t("fileUploadPage.documents")}
                accept={DOCUMENT_ACCEPT}
                files={disabledFiles}
                isMultiple={true}
                isDisabled={true}
            />
        </Preview>
    </>;
};

const FileUploadPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const documents = useUploadFiles();

    const contract = useUploadFiles();

    const avatar = useUploadFiles();

    const cover = useUploadFiles();

    const gallery = useUploadFiles();

    const [
        rejections,
        setRejections
    ] = useState<Array<IFileRejection>>([]);

    const getRulesCaption = () => {
        if(!rejections.length) {
            return t("fileUploadPage.rulesCaption");
        }

        return t("fileUploadPage.rulesRejected", [
            rejections.map((rejection) => `${rejection.name} (${rejection.code})`).join(", ")
        ]);
    };

    return <Article>
        <ComponentHeader
            lead={t("fileUploadPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("fileUploadPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("fileUploadPage.overviewPlatformText")}
            </Paragraph>
            <Preview
                caption={t("fileUploadPage.overviewCaption")}
                isStacked={true}
            >
                <FileUpload
                    title={t("fileUploadPage.documents")}
                    onChange={documents.setFiles}
                    maxFiles={DOCUMENT_MAX_FILES}
                    maxSize={DOCUMENT_MAX_SIZE}
                    accept={DOCUMENT_ACCEPT}
                    files={documents.files}
                    isMultiple={true}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("fileUploadPage.variantsText")}
            </Paragraph>
            <Preview
                caption={t("fileUploadPage.variantsCaption")}
                isStacked={true}
            >
                <FileUpload
                    title={t("fileUploadPage.contract")}
                    onChange={contract.setFiles}
                    maxSize={CONTRACT_MAX_SIZE}
                    accept={CONTRACT_ACCEPT}
                    files={contract.files}
                    variant="button"
                />
                <FileUpload
                    onChange={avatar.setFiles}
                    maxSize={AVATAR_MAX_SIZE}
                    title={t("sample.name")}
                    accept={IMAGE_ACCEPT}
                    files={avatar.files}
                    variant="avatar"
                    size="large"
                />
            </Preview>
            <CodeBlock
                code={VARIANTS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("fileUploadPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <FileUpload
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("fileUploadPage.rulesTitle")}
            id="rules"
        >
            <Paragraph>
                {t("fileUploadPage.rulesText")}
            </Paragraph>
            <Preview
                caption={getRulesCaption()}
                isStacked={true}
            >
                <FileUpload
                    title={t("fileUploadPage.cover")}
                    onChange={cover.setFiles}
                    maxSize={COVER_MAX_SIZE}
                    onReject={setRejections}
                    accept={IMAGE_ACCEPT}
                    files={cover.files}
                />
            </Preview>
            <CodeBlock
                code={RULES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("fileUploadPage.statesText")}
            </Paragraph>
            <StatesDemo
                key={language}
            />
            <CodeBlock
                code={STATES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("fileUploadPage.galleryTitle")}
            id="gallery"
        >
            <Paragraph>
                {t("fileUploadPage.galleryText")}
            </Paragraph>
            <Preview
                caption={t("fileUploadPage.galleryCaption")}
                isStacked={true}
            >
                <FileUpload
                    title={t("fileUploadPage.photos")}
                    maxFiles={GALLERY_MAX_FILES}
                    onChange={gallery.setFiles}
                    accept={IMAGE_ACCEPT}
                    files={gallery.files}
                    isMultiple={true}
                />
            </Preview>
            <CodeBlock
                code={GALLERY_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("fileUploadPage.a11yGroup"),
                    t("fileUploadPage.a11yKeyboard"),
                    t("fileUploadPage.a11yStatus"),
                    t("fileUploadPage.a11yRejection"),
                    t("fileUploadPage.a11yRows"),
                    t("fileUploadPage.a11yVariants")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("fileUploadPage.propFiles"),
                        type: "Array<IUploadFile>",
                        defaultValue: "[]",
                        name: "files"
                    },
                    {
                        description: t("fileUploadPage.propOnChange"),
                        type: "(files: Array<IUploadFile>) => void",
                        name: "onChange"
                    },
                    {
                        description: t("fileUploadPage.propOnRemove"),
                        type: "(key: string) => void",
                        name: "onRemove"
                    },
                    {
                        description: t("fileUploadPage.propOnRetry"),
                        type: "(key: string) => void",
                        name: "onRetry"
                    },
                    {
                        type: "(rejections: Array<IFileRejection>) => void",
                        description: t("fileUploadPage.propOnReject"),
                        name: "onReject"
                    },
                    {
                        description: t("fileUploadPage.propOnSourcePress"),
                        type: "(source: FileUploadSource) => void",
                        name: "onSourcePress"
                    },
                    {
                        type: "\"dropzone\" | \"button\" | \"avatar\"",
                        description: t("fileUploadPage.propVariant"),
                        defaultValue: "\"dropzone\"",
                        name: "variant"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("fileUploadPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("fileUploadPage.propIsMultiple"),
                        defaultValue: "false",
                        name: "isMultiple",
                        type: "boolean"
                    },
                    {
                        description: t("fileUploadPage.propMaxFiles"),
                        defaultValue: "10",
                        name: "maxFiles",
                        type: "number"
                    },
                    {
                        description: t("fileUploadPage.propMaxSize"),
                        name: "maxSize",
                        type: "number"
                    },
                    {
                        description: t("fileUploadPage.propAccept"),
                        type: "Array<string>",
                        name: "accept"
                    },
                    {
                        type: "Array<\"camera\" | \"library\" | \"files\">",
                        description: t("fileUploadPage.propSources"),
                        name: "sources"
                    },
                    {
                        description: t("fileUploadPage.propTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("fileUploadPage.propDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("fileUploadPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("fileUploadPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("fileUploadPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                {t("fileUploadPage.fileIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("fileUploadPage.fileKey"),
                        type: "string",
                        name: "key"
                    },
                    {
                        description: t("fileUploadPage.fileName"),
                        type: "string",
                        name: "name"
                    },
                    {
                        description: t("fileUploadPage.fileSize"),
                        type: "number",
                        name: "size"
                    },
                    {
                        type: "\"queued\" | \"uploading\" | \"processing\" | \"done\" | \"error\"",
                        description: t("fileUploadPage.fileStatus"),
                        name: "status"
                    },
                    {
                        description: t("fileUploadPage.fileProgress"),
                        name: "progress",
                        type: "number"
                    },
                    {
                        description: t("fileUploadPage.fileErrorText"),
                        name: "errorText",
                        type: "string"
                    },
                    {
                        description: t("fileUploadPage.filePreviewUri"),
                        name: "previewUri",
                        type: "string"
                    },
                    {
                        description: t("fileUploadPage.fileFile"),
                        name: "file",
                        type: "File"
                    }
                ]}
            />
            <Paragraph>
                {t("fileUploadPage.rejectionIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("fileUploadPage.rejectionCode"),
                        type: "\"type\" | \"size\" | \"count\"",
                        name: "code"
                    },
                    {
                        description: t("fileUploadPage.rejectionMessage"),
                        name: "message",
                        type: "string"
                    },
                    {
                        description: t("fileUploadPage.rejectionName"),
                        type: "string",
                        name: "name"
                    },
                    {
                        description: t("fileUploadPage.rejectionSize"),
                        type: "number",
                        name: "size"
                    },
                    {
                        description: t("fileUploadPage.rejectionFile"),
                        name: "file",
                        type: "File"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("fileUploadPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default FileUploadPage;
